// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { xScreen } from "@bg/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

const enum fields {
    sectionTitle = "sectionTitle",
    items = "items",
}

const enum numberFields {
    number = "number",
    title = "title",
    description = "description",
}

interface Item {
    fields: {
        title: string;
        number: number;
        description: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.sectionTitle]: string;
        [fields.items]: Item[];
    };
}

type Props = OwnProps;

export interface AboutUsNumbersWidgetStyles {
    sectionTitle?: TypographyProps;
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    numberTitleWrapper?: InjectableCss;
    numberText?: TypographyProps;
    titleText?: TypographyProps;
    descriptionText?: TypographyProps;
}

export const styles: AboutUsNumbersWidgetStyles = {
    sectionTitle: {
        variant: "h3",
        css: css`
            letter-spacing: 0;
            margin-bottom: 45px;
        `,
    },
    container: {
        css: css`
            max-width: 659px;
            padding-left: 40px;
            border-left: 1px solid ${getColor("secondary")};
            @media (max-width: 768px) {
                border: 0px;
                padding-left: 0;
            }
        `,
    },
    gridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        `,
    },
    numberTitleWrapper: {
        css: css`
            display: flex;
            align-items: baseline;
        `,
    },
    numberText: {
        color: "primary",
        variant: "h1",
        as: "h4",
        css: css`
            letter-spacing: 0;
            white-space: nowrap;
            margin-right: 6px;
            margin-bottom: 0;
        `,
    },
    titleText: {
        variant: "h5",
        css: css`
            letter-spacing: 0.31px;
        `,
    },
    descriptionText: {
        size: fontConfig.fontSize.small,
        lineHeight: "20px",
        css: css`
            letter-spacing: 0;
        `,
    },
};

const AboutUsNumbersWidget = ({ fields }: Props) => {
    return (
        <StyledWrapper
            css={css`
                padding-right: 36px;
                margin-top: 19px;
                @media (max-width: 768px) {
                    margin-bottom: 35px;
                    padding-right: 0;
                    margin-top: 0;
                }
            `}
        >
            <Typography {...styles.sectionTitle}>{fields.sectionTitle}</Typography>
            <GridContainer {...styles.container}>
                {fields.items.map(({ fields: { ...item } }) => (
                    <GridItem width={[12, 6, 6, 6, 6]} key={item.title} {...styles.gridItem}>
                        <StyledWrapper {...styles.numberTitleWrapper}>
                            <Typography {...styles.numberText}>{item.number}</Typography>
                            <Typography {...styles.titleText}>{item.title}</Typography>
                        </StyledWrapper>
                        <Typography {...styles.descriptionText}>{item.description}</Typography>
                    </GridItem>
                ))}
            </GridContainer>
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "XNGAGE" as any,
    displayName: "About Us Numbers",
    fieldDefinitions: [
        {
            displayName: "Section Title",
            name: fields.sectionTitle,
            defaultValue: "",
            editorTemplate: "TextField",
            fieldType: "Translatable",
        },
        {
            displayName: "Items",
            name: fields.items,
            editorTemplate: "ListField",
            defaultValue: [],
            getDisplay: (item: HasFields) => {
                return `${item.fields.number} ${item.fields.title}`;
            },
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    displayName: "Title",
                    name: numberFields.title,
                    defaultValue: "",
                    editorTemplate: "TextField",
                },
                {
                    displayName: "Number",
                    name: numberFields.number,
                    defaultValue: "",
                    editorTemplate: "TextField",
                },
                {
                    displayName: "Description",
                    name: numberFields.description,
                    defaultValue: "",
                    editorTemplate: "TextField",
                },
            ],
        },
    ],
};

const widgetModule: WidgetModule = {
    component: AboutUsNumbersWidget,
    definition,
};

export default widgetModule;
